<script setup lang="ts">
import FileListItem from './components/FileListItem.vue';
import { computed, onUnmounted, reactive, unref } from 'vue';
import { downloadFile } from '@/utils/download';
import type { AttachmentProps } from '../types';
import { attachments } from '@/components/icons/icons';
import { uniqueFileName } from '@/utils/stringUtils';
import { defaultFileUploadOptions, UploadProgress, useFileUpload } from '@/utils/useFileUpload';
import { NIL } from 'uuid';
import { truthy } from '@/utils/helper';
import { useFileDialog } from '@vueuse/core';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { FileMetaInfoDto } from '@/api/generator';

interface FilesToUpload {
  file?: File;
  name: string;
}

const props = withDefaults(defineProps<AttachmentProps>(), {
  mode: 'edit',
});

const emit = defineEmits<{
  'update:modelValue': [FileMetaInfoDto[]];
  'update:uploadProgress': [UploadProgress[]];
}>();

const {
  open,
  reset,
  onChange: onFileDialogChange,
} = useFileDialog({
  accept: '*',
  multiple: true,
});

const { uploadFile } = useFileUpload({
  ...defaultFileUploadOptions,
});

const fileInfos = computed({
  get() {
    return props.modelValue || [];
  },
  set(newValue) {
    update(newValue);
  },
});

const uploadProgresses = reactive(new Map<string, UploadProgress>());
const uploadFiles = reactive(new Map<string, File>());

const uploadState = computed(() => {
  //:data-testid="file.uploadProgress?.state"
  if (
    Array.from(uploadProgresses.values()).some(
      (uploadProgress) => uploadProgress.state === 'pending',
    )
  )
    return 'pending';

  if (
    Array.from(uploadProgresses.values()).some(
      (uploadProgress) => uploadProgress.state === 'failed',
    )
  )
    return 'failed';

  if (
    Array.from(uploadProgresses.values()).some(
      (uploadProgress) => uploadProgress.state === 'success',
    )
  )
    return 'success';

  return 'none';
});

function update(newFileInfos: FileMetaInfoDto[]) {
  emit('update:modelValue', newFileInfos);
}

async function remove(fileInfo: FileMetaInfoDto) {
  const targetProgress = uploadProgresses.get(fileInfo.name);
  if (targetProgress) {
    await abortUpload(targetProgress);
    uploadProgresses.delete(fileInfo.name);
  }
  emit('update:uploadProgress', Array.from(unref(uploadProgresses).values()));
  update(fileInfos.value.filter((f) => f.name != fileInfo.name));
}

async function abortUpload(progress: UploadProgress) {
  if (progress.state === 'pending') {
    try {
      await progress.abort?.(false);
    } catch (e) {
      console.error(e);
    }
  }
}

async function upload(filesToUpload: FilesToUpload[]) {
  await Promise.all([
    filesToUpload.map((fileToUpload) => {
      if (fileToUpload.file) {
        uploadFiles.set(fileToUpload.name, fileToUpload.file);
        return uploadFile({
          file: fileToUpload.file,
          name: fileToUpload.name,
          onUpdate: (uploadProgress) => {
            uploadProgresses.set(fileToUpload.name, uploadProgress);
            emit('update:uploadProgress', Array.from(unref(uploadProgresses).values()));
          },
          onReceivedId: (id: string) => {
            update(
              fileInfos.value.map((fileInfo) => {
                if (fileInfo.name === fileToUpload.name) {
                  return { ...fileInfo, id };
                } else return fileInfo;
              }),
            );
          },
        });
      }
    }),
  ]);
}

function retry(fileInfo: FileMetaInfoDto) {
  if (uploadFiles.has(fileInfo.name)) {
    upload([
      {
        name: fileInfo.name,
        file: uploadFiles.get(fileInfo.name),
      },
    ]);
  }
}

onFileDialogChange(async (files: FileList | null) => {
  if (files) {
    // Already available files in list
    const existingFileNames = fileInfos.value.map((fileInfo) => fileInfo.name).filter(truthy);

    const filesToUpload: FilesToUpload[] = [];

    const newFiles = Array.from(files)
      .reverse()
      .map((file) => {
        const uniqueName = uniqueFileName(file.name, existingFileNames);
        uploadProgresses.set(uniqueName, { state: 'idle' });
        filesToUpload.push({ name: uniqueName, file });

        const fileMetaInfo: FileMetaInfoDto = {
          name: uniqueName,
          id: NIL,
          size: file.size,
          type: file.type || '_', // we need this to undermine the type-minlength check, if a file with missing extension is uploaded
        };

        return fileMetaInfo;
      });

    update([...newFiles, ...fileInfos.value]);
    await upload(filesToUpload);
    reset();
  }
});

onUnmounted(() => {
  Array.from(uploadProgresses.values()).forEach((upload) => {
    abortUpload(upload);
  });
});
</script>

<template>
  <div :overallState="uploadState" :data-testid="$attrs['data-testid']">
    <div v-if="mode !== 'details'">
      <span class="uploadPart">
        <tooltip>
          <template #target="{ props: tooltipProps }">
            <v-btn
              size="24"
              v-bind="tooltipProps"
              data-testid="attachments_upload_btn"
              variant="flat"
              @click="open"
            >
              <v-icon size="24">{{ attachments }}</v-icon>
            </v-btn>
          </template>
          <template #tooltip>
            {{ $t('AddAttachments') }}
          </template>
        </tooltip>

        <span
          v-if="errorMessage"
          data-testid="attachments_errorMsg"
          class="ml-1 text-caption text-error"
        >
          {{ errorMessage }}
        </span>
      </span>

      <v-divider
        v-if="modelValue?.length"
        color="primary-lighten-4"
        class="mt-1 mb-1"
        thickness="1"
      ></v-divider>
    </div>

    <v-list v-if="fileInfos.length" class="filelist">
      <FileListItem
        v-for="file in fileInfos"
        :key="file.name"
        :file="file"
        :mode="mode"
        :uploadProgress="uploadProgresses.get(file.name)"
        @download="downloadFile"
        @remove="remove"
        @retry="retry"
      />
    </v-list>
  </div>
</template>

<style scoped lang="scss">
.uploadPart {
  display: flex;
  align-items: center;
}

.filelist {
  color: rgb(var(--v-theme-secondary-darken-2));
  padding: 0;
  background: transparent;
  overflow-x: hidden;
}
</style>
